import { Link2, FolderOpen } from "iconsax-reactjs";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { shallowEqual } from "react-redux";
import config from "src/config";
import { api } from "@/services/api";
import { logger } from "@/services/logger";
import {
  selectAppointmentFiles,
  selectAppointmentLinks,
  selectAppointmentPatientId,
} from "@/store/features/appointment/selectors";
import { useAppSelector } from "@/store/hooks";
import { Empty, Loading } from "@/ui";
import { downloadFileFromUrl } from "@/utils/file";
import { validateLink } from "@/utils/link";
import { showErrorToast } from "@/utils/toast";
import { StyledFileItem, StyledFilesBlock, StyledFilesLinks } from "./styled";

const FilesLinks: FC = () => {
  const {t} = useTranslation();

  const [loading, setLoading] = useState(false);

  const patientId = useAppSelector(selectAppointmentPatientId);

  const files = useAppSelector(
    selectAppointmentFiles,
    shallowEqual,
  );

  const links = useAppSelector(
    selectAppointmentLinks,
    shallowEqual,
  );

  const downloadFile = async (value: string) => {
    try {
      if (loading) {
        return;
      }

      setLoading(true);
      const res = await api.get(
        `storage/file/${value}?user_id=${patientId}`,
        {
          baseURL: config.file_url,
        }
      );

      const { url } = res.data.data;

      downloadFileFromUrl(url, value);
    } catch (error) {
      logger.error(error);
      showErrorToast(t('error'), t('something_went_wrong'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <StyledFilesLinks>
      {!files.length && !links.length ? (
        <Empty
          title={t('no_files_or_links')}
          text={t('no_files_or_links_message')}
        />
      ) : (
        <>
          {!!files.length && (
            <StyledFilesBlock>
              <h3>{t('files')}</h3>
              {files.map((item) => (
                <StyledFileItem key={`file-${item.id}`}>
                  <FolderOpen size={18} />
                  <span onClick={() => downloadFile(item.file)}>
                    {item.file}
                  </span>
                </StyledFileItem>
              ))}
            </StyledFilesBlock>
          )}
          {!!links.length && (
            <StyledFilesBlock>
              <h3>{t('links')}</h3>
              {links.map((item) => (
                <StyledFileItem key={`link-${item}`}>
                  <Link2 size={18} />
                  <a href={validateLink(item)} target="_blank" rel="noreferrer">{item}</a>
                </StyledFileItem>
              ))}
            </StyledFilesBlock>
          )}
        </>
      )}
      {loading && <Loading text={t('downloading')} />}
    </StyledFilesLinks>
  );
};

export default FilesLinks;
